import { supabase } from '@/integrations/supabase/client';

/**
 * Clean a highlight text using LLM with custom user prompt
 * @param highlight The raw highlight text to clean
 * @param userPrompt User's custom instructions for cleaning
 * @returns Cleaned text from LLM
 * @throws Error if LLM call fails
 */
export async function cleanHighlightWithLLM(
  highlight: string,
  userPrompt: string
): Promise<string> {
  try {
    // Get current session token
    const { data: { session } } = await supabase.auth.getSession();

    if (!session) {
      throw new Error('No active session');
    }

    // Construct system prompt with user's instructions
    const systemPrompt = `You are a text cleanup assistant. Your job is to transform raw text highlights into clean, concise, well-written descriptions.

User's instructions: ${userPrompt}

Guidelines:
- Follow the user's instructions exactly
- Keep output concise and clear
- Preserve key information from the original text
- Remove formatting artifacts and noise
- Do not add information not present in the original
- Output ONLY the cleaned text, no explanations or meta-commentary`;

    // Call ai-composer edge function
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-composer`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          systemPrompt: systemPrompt,
          userPrompt: highlight, // The raw highlight to clean
          maxTokens: 500,
          temperature: 0.3, // Lower temp for consistency
          useGemini: true, // Use Gemini for cost efficiency
        }),
      }
    );

    if (!response.ok) {
      const error = await response.text();
      throw new Error(`LLM API error: ${error}`);
    }

    const result = await response.json();

    // ai-composer returns { text: string }
    const cleanedText = result.text;

    if (!cleanedText) {
      throw new Error('No cleaned text in LLM response');
    }

    return cleanedText.trim();
  } catch (error) {
    console.error('Error calling LLM cleanup:', error);
    throw error;
  }
}
